import type { ChatProductItem } from '../providers/types';
import type { Product } from '../types/api';

/**
 * Coerce a raw attribute value into an array of product-like records.
 * Accepts an array, a single object, or a JSON string (stringified array/object).
 */
export function safeParseJson(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return [raw];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return [parsed];
      return [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * A card's `variant_id` as a positive integer, or undefined. Accepts a number or
 * a digit-only string; anything else is ignored so the card opens the product.
 */
export function parseVariantId(raw: unknown): number | undefined {
  if (typeof raw === 'number') return Number.isSafeInteger(raw) && raw > 0 ? raw : undefined;
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    const id = Number(raw.trim());
    return Number.isSafeInteger(id) && id > 0 ? id : undefined;
  }
  return undefined;
}

/** `{ variantId }` when the card names a variant, `{}` otherwise. */
function variantField(p: Record<string, unknown>): Pick<ChatProductItem, 'variantId'> {
  const variantId = parseVariantId(p.variant_id);
  return variantId === undefined ? {} : { variantId };
}

/**
 * Normalizes product data from Twilio Conversations message attributes into a
 * unified ChatProductItem[]. Two payload shapes are supported:
 *
 *  - Variant A (seller-sent): `attributes.type === 'product'`, `attributes.json`
 *    is a stringified array of products.
 *  - Variant B (AI bot):      `attributes.shared.type === 'product'`,
 *    `attributes.shared.json` is an already-parsed array.
 *
 * Returns null when the message is not a product message.
 *
 * NOTE: This is the single source of truth for product-message parsing, shared
 * by RichMessage (chat rendering) and ConsultationScreen (soft-focus routing).
 */
export function normalizeProducts(attributes: Record<string, unknown>): ChatProductItem[] | null {
  // Variant A: seller-sent products
  if (attributes.type === 'product') {
    const parsed = safeParseJson(attributes.json);
    if (parsed.length === 0) return null;
    return parsed.map((item: unknown) => {
      const p = item as Record<string, unknown>;
      return {
        id: (p.id as number | string) ?? '',
        ...variantField(p),
        name: (p.name as string) ?? '',
        price: (p.price as string) ?? null,
        imageUrl: (p.image_url as string) ?? (p.image as string) ?? null,
        url: (p.url as string) ?? null,
      };
    });
  }

  // Variant B: AI Bot products
  const shared = attributes.shared as Record<string, unknown> | undefined;
  if (shared && shared.type === 'product') {
    const raw = shared.json;
    const parsed: unknown[] = safeParseJson(raw);
    if (parsed.length === 0) return null;
    return parsed.map((item: unknown) => {
      const p = item as Record<string, unknown>;
      return {
        id: (p.id as number | string) ?? '',
        ...variantField(p),
        name: (p.name as string) ?? '',
        price: (p.price as string) ?? null,
        // Variant B uses 'image' field
        imageUrl: (p.image as string) ?? (p.image_url as string) ?? null,
        url: (p.url as string) ?? null,
      };
    });
  }

  return null;
}

/**
 * Convert a ChatProductItem (from a suggestion message) into a catalog Product
 * so it can be merged into the products list rendered by ProductsPanel.
 *
 * Returns null when the item lacks a usable numeric id — Product.id must be a
 * positive number (the PDP fetch + cart/wishlist APIs key on it). Non-numeric
 * ids (some AI-bot payloads) are skipped for list insertion.
 */
export function chatProductToProduct(item: ChatProductItem): Product | null {
  const id = typeof item.id === 'number' ? item.id : Number(item.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    title: item.name || '',
    price: item.price ?? '',
    image_url: item.imageUrl ?? '',
  };
}
